import React from 'react'
import { Box, Stack } from '@mui/material'
import { Card, Skeleton } from '@repo/ui'
import { STATUS_TOKEN_H } from 'components'

import { PROSE_LINE_H, ProseSkeleton } from '../ProseSkeleton'

/**
 * Loading state for the Search / Filter / Sort row, drawn WHERE THE REAL ONE LIVES.
 *
 * Split out of `SupportPageSkeleton` when the toolbar moved inside the page's sticky header:
 * the real `SupportListControls` is now a child of that block, so its stand-in has to be one
 * too. Left in the page skeleton it would have drawn below the header instead of inside it —
 * a different footprint, which is exactly the layout shift §19 exists to prevent, and it would
 * have pushed every card on a list whose skeleton is currently pixel-exact.
 *
 * 66px of footprint, not the 49 an earlier version drew: `ActionLine` is a 50px search field
 * beside two 49px buttons AND carries its own `pb={4}`, and that 16px of bottom padding belongs
 * to the toolbar. Leaving it out started every card 17px high.
 */
export const SupportListControlsSkeleton = () => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    spacing={3}
    sx={{ pb: 4 }}
  >
    <Skeleton width={300} height={50} />
    <Stack direction="row" spacing={3}>
      <Skeleton width={100} height={49} />
      <Skeleton width={92} height={49} />
    </Stack>
  </Stack>
)

/**
 * Loading state for a request list (working-conventions §19: skeletons, never spinners,
 * and the skeleton occupies the layout the content will land in).
 *
 * Mirrors the real page region for region — a stack of request cards at the same height — so
 * the swap to real content causes no layout shift. The toolbar above them is NOT drawn here;
 * it is `SupportListControlsSkeleton`, rendered by the page inside the sticky header, for the
 * reason given on that component.
 */
export const SupportPageSkeleton = ({
  scope,
}: {
  /**
   * Which list is loading. Not cosmetic: the company list puts a "Raised by <name>" line on
   * every card and the personal one does not, which is 30px of card height (an 18px `body4`
   * line plus the 12px stack gap). A skeleton that guessed would be wrong on one of the two
   * surfaces by that much per row — 14 rows deep on the seeded list, that is a 420px jump.
   * `SupportPage` knows the scope, so it says.
   */
  scope: 'mine' | 'company'
}) => (
  <Stack spacing={4}>
    {[0, 1, 2, 3].map(i => (
      <Card key={i} sx={{ p: 4, mb: 0 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={3} alignItems="center">
            {/* Type badge, status pill, reference — then the date hard right, as on the card.
                Both tokens take their height from the component they stand in for rather than a
                literal, so the row cannot drift out of step with it again. */}
            <Skeleton width={86} height={STATUS_TOKEN_H} />
            <Skeleton width={56} height={STATUS_TOKEN_H} />
            <Skeleton width={36} height={18} />
            <Box sx={{ flex: 1 }} />
            <Skeleton width={140} height={18} />
          </Stack>
          {/* One line, though the card clamps at two: all 14 seeded request bodies render on a
              single line at this column width. Each additional rendered line is 21px of shift,
              so re-measure if bodies get longer. */}
          <ProseSkeleton lines={1} />
          {scope === 'company' && (
            <ProseSkeleton lines={1} lineHeight={PROSE_LINE_H.body4} />
          )}
        </Stack>
      </Card>
    ))}
  </Stack>
)
